
import React from 'react';

interface Deal {
  businessName: string;
  businessLogo: string;
  address: string;
  phone: string;
  website?: string;
  hours?: string;
  rating: number;
  reviews: number;
  mapQuery?: string;
}

interface BusinessCardProps {
  deal: Deal;
}

/**
 * BusinessCard – renders a card with business information.
 * Includes defensive checks for optional fields and basic error handling
 * for malformed data (e.g., missing phone number).
 */
export default function BusinessCard({ deal }: BusinessCardProps) {
  // Guard against an undefined or empty phone string – fallback to "#" link
  const sanitizedPhone = deal.phone
    ? deal.phone.replace(/[^0-9+]/g, '')
    : '';

  const phoneHref = sanitizedPhone ? `tel:${sanitizedPhone}` : '#';

  // Ensure rating is a number between 0 and 5
  const safeRating = typeof deal.rating === 'number' && deal.rating >= 0 && deal.rating <= 5
    ? deal.rating
    : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-5">Business Information</h2>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className={`${deal.businessLogo} text-teal-600 text-2xl`}></i>
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">{deal.businessName}</h3>
          <div className="flex items-center gap-1 mt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <i
                key={i}
                className={`text-xs ${
                  i < Math.floor(safeRating)
                    ? 'ri-star-fill text-amber-400'
                    : i < safeRating
                    ? 'ri-star-half-fill text-amber-400'
                    : 'ri-star-line text-gray-300'
                }`}
              ></i>
            ))}
            <span className="text-xs text-gray-500 ml-1">
              {safeRating} ({deal.reviews})
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <a
          href={phoneHref}
          className="flex items-center gap-3 group cursor-pointer"
        >
          <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-50 transition-colors">
            <i className="ri-phone-line text-gray-500 group-hover:text-teal-600 text-base transition-colors"></i>
          </div>
          <div>
            <p className="text-xs text-gray-400">Phone</p>
            <p className="text-sm text-gray-800 font-medium group-hover:text-teal-700 transition-colors">
              {deal.phone}
            </p>
          </div>
        </a>

        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-map-pin-line text-gray-500 text-base"></i>
          </div>
          <div>
            <p className="text-xs text-gray-400">Address</p>
            <p className="text-sm text-gray-800 font-medium">{deal.address}</p>
          </div>
        </div>

        {deal.website && (
          <a
            href={`https://${deal.website}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-3 group cursor-pointer"
          >
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-50 transition-colors">
              <i className="ri-global-line text-gray-500 group-hover:text-teal-600 text-base transition-colors"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">Website</p>
              <p className="text-sm text-gray-800 font-medium group-hover:text-teal-700 transition-colors">
                {deal.website}
              </p>
            </div>
          </a>
        )}

        {deal.hours && (
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-time-line text-gray-500 text-base"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">Business Hours</p>
              <p className="text-sm text-gray-800 font-medium">{deal.hours}</p>
            </div>
          </div>
        )}
      </div>

      <a
        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          deal.address
        )}`}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-direction-line text-sm"></i>
        </div>
        Get Directions
      </a>
    </div>
  );
}
